/* Copyright (c) Trimble Inc. All rights reserved. */

import { MouseEvent, useCallback, useContext, useState } from 'react';
import { useUserStore } from '../../stores/User.store';
import { UserPanelModel } from './UserPanel.model';
import { AuthContext, AuthContextState } from '../../config/AuthConsumerContext.config';
import { getEnvUrl } from '../../config/external.config';
import { externalRedirect } from '@tcp-base/design-system';
import { ModusWcMenuItem } from '@trimble-oss/moduswebcomponents/components/modus-wc-menu-item';
import AuthRedirectorHelper from '../../helpers/AuthRedirector.helper';

export const useUserPanelController = (): UserPanelModel => {
  const userProfile = useUserStore((state) => state.userProfile);
  const [isLegalContentEnabled, setIsLegalContentEnabled] = useState(false);
  const [isPanelMenuVisible, setIsPanelMenuVisible] = useState(false);

  const context = useContext<AuthContextState | null>(AuthContext);

  const toggleLegalContentVisibility = useCallback(() => {
    setIsLegalContentEnabled(!isLegalContentEnabled);
  }, [isLegalContentEnabled, setIsLegalContentEnabled]);

  const togglePanelVisibility = useCallback(
    (_event: CustomEvent<{ isVisible: boolean }>) => {
      setIsPanelMenuVisible(!isPanelMenuVisible);
    },
    [isPanelMenuVisible, setIsPanelMenuVisible],
  );

  const navigateToLink = useCallback((event: MouseEvent<ModusWcMenuItem, MouseEvent>) => {
    externalRedirect(event.currentTarget.value);
  }, []);

  return {
    given_name: userProfile.given_name,
    family_name: userProfile.family_name,
    email: userProfile.email,
    picture: userProfile.picture,
    isProfileLoaded: !!(userProfile.email || userProfile.given_name || userProfile.family_name),
    isLegalContentEnabled,
    toggleLegalContentVisibility,
    isPanelMenuVisible,
    togglePanelVisibility,
    links: {
      profile: getEnvUrl('PROFILE_URL', context?.environment ?? 'PRODUCTION'),
      iotSupport: getEnvUrl('IOT_SUPPORT_URL', context?.environment ?? 'PRODUCTION'),
      transactionalEmail: getEnvUrl('TRANSACTION_EMAIL_URL', context?.environment ?? 'PRODUCTION'),
    },
    navigateToLink,
    signOut: (): void => {
      setIsPanelMenuVisible(false);
      AuthRedirectorHelper.logout({
        clearLocalStorage: true,
      }) as void;
    },
  };
};
